import { decodeClaims } from "./pkce.js";
import { SKEW_MS, TOKENS_KEY, type KeyValueStore, type StoredTokens, type TokenSource } from "./auth.js";

/**
 * In-house username/password sign-in against Amazon Cognito, using the
 * `InitiateAuth` / `RespondToAuthChallenge` JSON API directly (no Hosted UI,
 * no redirect). The SPA is a public client, so there is no client secret; the
 * password is sent over TLS to Cognito with the USER_PASSWORD_AUTH flow. The
 * endpoint is configurable (`idpUrl`) so the local runtime can emulate the same
 * protocol — the SPA code path is identical in local and deployed environments.
 *
 * Tokens are stored under the same key as the OAuth flow, so session handling
 * (AuthGate, ApiClient) is unchanged. Only the acquisition differs.
 */

export interface CognitoPasswordConfig {
  /** App client id (public client; USER_PASSWORD_AUTH must be enabled on it). */
  readonly clientId: string;
  /** Cognito IDP endpoint, e.g. https://cognito-idp.us-east-1.amazonaws.com/ */
  readonly idpUrl: string;
}

export interface CognitoPasswordEnv {
  readonly config: CognitoPasswordConfig;
  readonly storage: KeyValueStore;
  readonly fetch: typeof fetch;
  readonly now: () => number;
}

export type SignInResult =
  | { readonly status: "ok" }
  | { readonly status: "new_password_required"; readonly email: string; readonly session: string };

/** A Cognito error surfaced to the UI. `code` is the Cognito `__type` (e.g. NotAuthorizedException). */
export class CognitoError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = "CognitoError";
  }
}

/** Shape of a Cognito AuthenticationResult (only the fields we use). */
interface AuthenticationResult {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
}

interface InitiateAuthResponse {
  AuthenticationResult?: AuthenticationResult;
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

/** Map a few Cognito error codes to messages that make sense to an end user. */
function friendly(code: string, fallback: string): string {
  switch (code) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
      return "Incorrect email or password.";
    case "UsernameExistsException":
      return "An account with that email already exists. Try signing in instead.";
    case "CodeMismatchException":
      return "That code is not correct. Please check and try again.";
    case "ExpiredCodeException":
      return "That code has expired. Request a new one.";
    case "InvalidPasswordException":
      return fallback || "That password does not meet the policy requirements.";
    case "InvalidParameterException":
      return fallback || "Please check the details and try again.";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Please wait a moment and try again.";
    default:
      return fallback;
  }
}

export class CognitoPasswordAuth implements TokenSource {
  constructor(private readonly env: CognitoPasswordEnv) {}

  /** Start sign-in. Resolves to "ok" (tokens stored) or a required-action status. */
  async signInWithPassword(email: string, password: string): Promise<SignInResult> {
    const r = await this.call<InitiateAuthResponse>("InitiateAuth", {
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: this.env.config.clientId,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    });
    return this.fromResponse(r, email);
  }

  /** Complete a NEW_PASSWORD_REQUIRED challenge (first sign-in after an admin invite). */
  async completeNewPassword(email: string, newPassword: string, session: string): Promise<SignInResult> {
    const r = await this.call<InitiateAuthResponse>("RespondToAuthChallenge", {
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      ClientId: this.env.config.clientId,
      Session: session,
      ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPassword },
    });
    return this.fromResponse(r, email);
  }

  /** Self-service sign-up with email + password. Cognito emails a confirmation
   * code; the caller then collects it and calls {@link confirmSignUp}. Returns
   * the masked delivery destination so the UI can tell the user where to look. */
  async signUp(email: string, password: string): Promise<{ destination?: string }> {
    const r = await this.call<{ CodeDeliveryDetails?: { Destination?: string } }>("SignUp", {
      ClientId: this.env.config.clientId,
      Username: email,
      Password: password,
      UserAttributes: [{ Name: "email", Value: email }],
    });
    return r.CodeDeliveryDetails?.Destination ? { destination: r.CodeDeliveryDetails.Destination } : {};
  }

  /** Confirm a self-service sign-up with the emailed code (proves email ownership). */
  async confirmSignUp(email: string, code: string): Promise<void> {
    await this.call("ConfirmSignUp", { ClientId: this.env.config.clientId, Username: email, ConfirmationCode: code });
  }

  /** Resend the sign-up confirmation code. Returns the masked delivery destination. */
  async resendSignUpCode(email: string): Promise<{ destination?: string }> {
    const r = await this.call<{ CodeDeliveryDetails?: { Destination?: string } }>("ResendConfirmationCode", { ClientId: this.env.config.clientId, Username: email });
    return r.CodeDeliveryDetails?.Destination ? { destination: r.CodeDeliveryDetails.Destination } : {};
  }

  /** Begin a password reset; Cognito emails a verification code. Returns the masked destination. */
  async forgotPassword(email: string): Promise<{ destination?: string }> {
    const r = await this.call<{ CodeDeliveryDetails?: { Destination?: string } }>("ForgotPassword", { ClientId: this.env.config.clientId, Username: email });
    return r.CodeDeliveryDetails?.Destination ? { destination: r.CodeDeliveryDetails.Destination } : {};
  }

  /** Complete a password reset with the emailed code and a new password. */
  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await this.call("ConfirmForgotPassword", { ClientId: this.env.config.clientId, Username: email, ConfirmationCode: code, Password: newPassword });
  }

  session(): StoredTokens | null {
    const raw = this.env.storage.getItem(TOKENS_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredTokens;
    } catch {
      this.env.storage.removeItem(TOKENS_KEY);
      return null;
    }
  }

  async currentToken(): Promise<string | null> {
    const s = this.session();
    if (!s) return null;
    if (s.expiresAt - SKEW_MS > this.env.now()) return s.accessToken;
    if (!s.refreshToken) {
      this.clear();
      return null;
    }
    try {
      const r = await this.call<InitiateAuthResponse>("InitiateAuth", {
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: this.env.config.clientId,
        AuthParameters: { REFRESH_TOKEN: s.refreshToken },
      });
      if (!r.AuthenticationResult) {
        this.clear();
        return null;
      }
      // Refresh responses carry no new refresh token; keep the existing one.
      const next = this.toStored(r.AuthenticationResult, s.refreshToken);
      this.store(next);
      return next.accessToken;
    } catch {
      this.clear();
      return null;
    }
  }

  clear(): void {
    this.env.storage.removeItem(TOKENS_KEY);
  }

  /** No hosted logout to redirect to; the caller resets to the sign-in screen. */
  signOut(): void {
    this.clear();
  }

  private fromResponse(r: InitiateAuthResponse, email: string): SignInResult {
    if (r.ChallengeName === "NEW_PASSWORD_REQUIRED") {
      if (!r.Session) throw new CognitoError("invalid_response", "The identity provider did not return a challenge session.");
      return { status: "new_password_required", email, session: r.Session };
    }
    if (r.AuthenticationResult) {
      this.store(this.toStored(r.AuthenticationResult, null));
      return { status: "ok" };
    }
    throw new CognitoError("unsupported_challenge", `Unsupported sign-in step: ${r.ChallengeName ?? "unknown"}.`);
  }

  private toStored(ar: AuthenticationResult, priorRefresh: string | null): StoredTokens {
    if (!ar.AccessToken) throw new CognitoError("invalid_response", "The identity provider returned no access token.");
    const claims = decodeClaims(ar.AccessToken);
    const idClaims = ar.IdToken ? decodeClaims(ar.IdToken) : null;
    const sub = (claims?.["sub"] ?? idClaims?.["sub"]) as string | undefined;
    if (!sub) throw new CognitoError("invalid_token", "The access token has no subject.");
    const exp = typeof claims?.["exp"] === "number" ? (claims["exp"] as number) * 1000 : this.env.now() + (ar.ExpiresIn ?? 3600) * 1000;
    return {
      accessToken: ar.AccessToken,
      idToken: ar.IdToken ?? null,
      refreshToken: ar.RefreshToken ?? priorRefresh,
      expiresAt: exp,
      sub,
      email: (idClaims?.["email"] as string | undefined) ?? null,
    };
  }

  private store(t: StoredTokens): void {
    this.env.storage.setItem(TOKENS_KEY, JSON.stringify(t));
  }

  private async call<T = Record<string, unknown>>(target: string, body: unknown): Promise<T> {
    const res = await this.env.fetch(this.env.config.idpUrl, {
      method: "POST",
      headers: {
        "content-type": "application/x-amz-json-1.1",
        "x-amz-target": `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
    });
    let json: Record<string, unknown> = {};
    try {
      json = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON body */
    }
    if (!res.ok) {
      const code = String(json["__type"] ?? "").split("#").pop() || "error";
      const message = String(json["message"] ?? `Request failed (${res.status}).`);
      throw new CognitoError(code, friendly(code, message));
    }
    return json as T;
  }
}
